import React, { useCallback, useEffect, useRef } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import CreativeCard from '../components/CreativeCard';
import CreativeTable from '../components/CreativeTable';
import EmptyState from '../components/EmptyState';
import ListSkeleton from '../components/ListSkeleton';

/**
 * Renders one tab's list: skeleton → error → empty → data, plus the
 * infinite-scroll sentinel and its always-visible "Load more" fallback.
 */
const CreativeList = ({
  tab,
  rows,
  total,
  hasMore,
  loading,
  loadingMore,
  error,
  onRetry,
  onLoadMore,
  onOpen,
  onPick,
  onUpload,
  busyId,
  filtered,
  onClearFilters,
}) => {
  const observerRef = useRef(null);
  const sentinelNodeRef = useRef(null);
  const loadMoreRef = useRef(onLoadMore);
  loadMoreRef.current = onLoadMore;

  // Stable ref callback: the observer is created once per sentinel node, not
  // once per render.
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    sentinelNodeRef.current = node || null;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMoreRef.current?.();
      },
      { rootMargin: '240px 0px' }
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  // An observer only fires on a *transition*. When a page lands without
  // pushing the sentinel out of view it would otherwise go quiet, so re-arm it
  // and let it re-evaluate. `loadMore` early-returns unless there is more to
  // fetch, which is what terminates this.
  useEffect(() => {
    const observer = observerRef.current;
    const node = sentinelNodeRef.current;
    if (!observer || !node || !hasMore || loading || loadingMore) return;
    observer.unobserve(node);
    observer.observe(node);
  }, [rows.length, hasMore, loading, loadingMore]);

  useEffect(
    () => () => {
      if (observerRef.current) observerRef.current.disconnect();
    },
    []
  );

  if (loading && rows.length === 0) return <ListSkeleton />;

  if (error && rows.length === 0) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50/60 px-5 py-10 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
          <SlIcon
            library="lucide"
            name="triangle-alert"
            className="text-rose-600"
            style={{ fontSize: '22px' }}
          />
        </div>
        <h3 className="text-sm font-semibold text-rose-900">Could not load this list</h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-rose-700">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
        >
          <SlIcon library="lucide" name="refresh-cw" style={{ fontSize: '14px' }} />
          Retry
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white">
        <EmptyState tab={tab} filtered={filtered} onClearFilters={onClearFilters} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CreativeTable
        rows={rows}
        onOpen={onOpen}
        onPick={onPick}
        onUpload={onUpload}
        busyId={busyId}
      />

      {/* Mobile-only stack. `md:hidden` is safe; `hidden md:block` is not. */}
      <div className="md:hidden space-y-3">
        {rows.map((row) => (
          <CreativeCard
            key={row.bsc_id}
            row={row}
            onOpen={onOpen}
            onPick={onPick}
            onUpload={onUpload}
            busy={Number(busyId) === Number(row.bsc_id)}
          />
        ))}
      </div>

      {/* An append that failed keeps the rows and offers a retry inline. */}
      {error && rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <span>{error}</span>
          <button
            type="button"
            onClick={onRetry}
            className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100"
          >
            Retry
          </button>
        </div>
      )}

      <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />

      <div className="flex flex-col items-center gap-2 pb-6 pt-1">
        {loadingMore && (
          <span className="inline-flex items-center gap-2 text-sm text-slate-500">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-sky-600" />
            Loading more…
          </span>
        )}
        {!loadingMore && hasMore && (
          <button
            type="button"
            onClick={onLoadMore}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            <SlIcon library="lucide" name="chevron-down" style={{ fontSize: '14px' }} />
            Load more
          </button>
        )}
        {!hasMore && !loadingMore && (
          <span className="text-xs text-slate-400">
            Showing all {rows.length} of {total || rows.length}
          </span>
        )}
      </div>
    </div>
  );
};

export default CreativeList;
